import { useAppSelector } from "../../../utils/hooks";
import { useEffect, useState } from "react";
import { useEffectiveMainStream } from "../../../hooks/useEffectiveMainStream";
import { useStreamAvatar } from "../../../hooks/useStreamAvatar";
import { useCallChrome } from "./CallChromeContext";

export default function UserNameAndTime() {
  const { headerVisible, callTypeLabel, logoSrc } = useCallChrome();
  const currentStream = useEffectiveMainStream();
  const userData = useAppSelector((state) => state.userData);
  const { name: connectedName } = useStreamAvatar(currentStream);

  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTime, setShowTime] = useState(false);
  const [inlineBadgeMounted, setInlineBadgeMounted] = useState(false);

  useEffect(() => {
    if (!headerVisible) {
      let cancelled = false;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!cancelled) setInlineBadgeMounted(true);
        });
      });
      return () => {
        cancelled = true;
      };
    }
    setInlineBadgeMounted(false);
    return undefined;
  }, [headerVisible]);

  useEffect(() => {
    const interval = setInterval(() => {
      const startTime = sessionStorage.getItem("callStartTime");
      if (!startTime) {
        setShowTime(false);
        return;
      }
      const now = Date.now();
      setElapsedTime(Math.floor((now - Number(startTime)) / 1000));
      setShowTime(true);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  let displayName: string | null = null;
  if (currentStream) {
    if (userData.user_id === currentStream.userId) {
      displayName = "You";
    } else if (connectedName) {
      displayName = connectedName;
    }
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex min-w-0 items-center justify-between gap-3 px-4 sm:top-5 sm:px-6 lg:px-8">
      {displayName ? (
        <div className="min-w-0 flex-1 overflow-hidden pr-1">
          <div className="inline-flex max-w-full min-w-0 items-center gap-2.5 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-sm font-medium text-white shadow-lg backdrop-blur-xl sm:px-4 sm:text-base">
            {!headerVisible && logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full border-2 border-white/40 object-cover"
              />
            ) : null}
            <span className="min-w-0 truncate text-left">{displayName}</span>
          </div>
        </div>
      ) : (
        <span className="min-w-0 flex-1" />
      )}

      <div className="flex shrink-0 items-center gap-2">
        {!headerVisible ? (
          <div
            className={`flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-sm font-medium text-white shadow-lg backdrop-blur-xl transition-all duration-500 ease-out sm:px-4 sm:text-base ${
              inlineBadgeMounted
                ? "translate-x-0 opacity-100"
                : "translate-x-6 opacity-0"
            }`}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="max-w-[42vw] truncate sm:max-w-none">
              {callTypeLabel}
            </span>
          </div>
        ) : null}

        {showTime ? (
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-sm font-medium text-white shadow-lg backdrop-blur-xl sm:px-4 sm:text-base">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
            <span className="tabular-nums">{formatTime(elapsedTime)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
